import { SettingStorage } from '@services/SettingStorage';
import { SurveyStatsFiltersProps, SurveyStatsStatus } from '../types';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { DataPeriods } from '@utils/dataPeriods';

const STORAGE_KEY = 'admin-survey-filters';

export const getDefaultFilters = (initiativeId?: string): SurveyStatsFiltersProps => {
  return {
    months: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
    years: [new Date().getFullYear().toString()],
    periods: [DataPeriods.Monthly, DataPeriods.Quarterly, DataPeriods.Yearly],
    initiatives: initiativeId ? [initiativeId] : [],
    surveyStatuses: [SurveyStatsStatus.Completed, SurveyStatsStatus.Pending],
  };
};

const resolveFilterValues = <T extends string>(parsedFilter: any, fallbackValues?: T[]) => {
  return Array.isArray(parsedFilter) && parsedFilter.length > 0 ? parsedFilter as T[] : fallbackValues;
};
/**
 * Load saved filters from localStorage for the current user
 */
export const loadSavedFilters = ({
  userId,
  initiativeId,
}: {
  userId: string;
  initiativeId: string;
}): SurveyStatsFiltersProps => {
  const stored = SettingStorage.getItem(`${STORAGE_KEY}-${userId}-${initiativeId}`);
  const defaults = getDefaultFilters(initiativeId);
  if (!stored) {
    return defaults;
  }
  try {
    const parsed: SurveyStatsFiltersProps = JSON.parse(stored);
    return {
      years: resolveFilterValues(parsed.years, defaults.years),
      months: resolveFilterValues(parsed.months, defaults.months),
      periods: resolveFilterValues(parsed.periods, defaults.periods),
      initiatives: resolveFilterValues(parsed.initiatives, defaults.initiatives),
      surveyStatuses: resolveFilterValues(parsed.surveyStatuses, defaults.surveyStatuses),
    };
  } catch (error) {
    console.error('Failed to parse saved filters', error);
    return defaults;
  }
};

/**
 * Save current filters to localStorage (manual save only)
 */
export const saveFilters = ({
  userId,
  initiativeId,
  filters,
}: {
  userId: string;
  initiativeId: string;
  filters: SurveyStatsFiltersProps;
}): void => {
  try {
    SettingStorage.setItem(`${STORAGE_KEY}-${userId}-${initiativeId}`, JSON.stringify(filters));
    generateToast({
      color: 'success',
      message: 'Filters saved successfully',
    });
  } catch (error) {
    toastFromError({ error, title: 'Failed to save filters' });
  }
};
